// Вокабуляр javelin: статус сессии в трекере.
//
// Статус выставляет менеджер в Google Sheets, часовой крон затягивает его в
// `participants.tracker_status`. Модератор может выставить только Cancelled и
// только пока менеджер не решил (см. participantSetTrackerStatus).
//
// ВОКАБУЛЯР ХРАНЕНИЯ — то, что РЕАЛЬНО лежит в колонке, включая мусор.
// Сверка с прод-D1 на 2026-08-15 (1366 строк):
//   Accepted 920 · Pending 242 · Rejected 34 · Cancelled 34 · Uploaded 27
//   Accpeted 11 · canceled 24 (в колонке status) · Canceled 1
//
// «Accpeted» — опечатка, приехавшая из таблицы. Она стоила бы дорого:
// правило блокировки сравнивает строки ТОЧНО, поэтому одиннадцать уже
// принятых сессий не были защищены и модератор мог их отменить. Ровно
// поэтому сравнивать статусы напрямую нельзя — только через canonTracker.

/** Канон: то, чем статус является по смыслу. */
export const TRACKER_STATUS = [
  'Accepted', 'Pending', 'Rejected', 'Cancelled', 'Uploaded',
  // 2026-09-05: модератор помечает так сессию, которой нужны фиксы. Для денег
  // это ОБЫЧНОЕ ожидание — бюджет списан, зарплата и касса считаются, KPI не
  // страдает. Отдельный статус нужен не расчёту, а людям: саппорт и IT видят,
  // что здесь требуется помощь, и такая сессия может легально повисеть.
  'To be fixed',
] as const;
export type TrackerStatus = (typeof TRACKER_STATUS)[number];

/** Исторические написания → канон. Ключи в нижнем регистре. */
const TRACKER_ALIASES: Record<string, TrackerStatus> = {
  accepted: 'Accepted',
  accpeted: 'Accepted',          // опечатка из таблицы, 11 строк в проде
  pending: 'Pending',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',         // одна «l» + нижний регистр, 25 строк
  uploaded: 'Uploaded',
  'to be fixed': 'To be fixed',
  tobefixed: 'To be fixed',
  'to_be_fixed': 'To be fixed',
  'to be fix': 'To be fixed',      // опечатка ждёт своего часа — см. «Accpeted»
  tbf: 'To be fixed',
};

/**
 * Статусы, за которые модератору платят: вердикта клиента ещё нет либо он
 * положительный. «To be fixed» здесь наравне с Pending — сессия снята,
 * участнику заплачено, а то, что ей нужны правки, к деньгам отношения не
 * имеет.
 *
 * Список нужен и в SQL: сравнений `IN ('Accepted','Pending')` в воркере шесть
 * штук в разных файлах, и появление шестого статуса — ровно тот случай, когда
 * они расходятся молча. Одна константа на всех.
 */
export const TRACKER_PAYABLE: readonly TrackerStatus[] = ['Accepted', 'Pending', 'To be fixed'];
export const TRACKER_PAYABLE_SQL = TRACKER_PAYABLE.map(s => `'${s}'`).join(',');

/** Сессия ждёт: вердикта нет, но и отказа нет. */
export function isPendingLike(raw: unknown): boolean {
  const c = canonTracker(raw);
  return c === 'Pending' || c === 'To be fixed';
}

/**
 * Строка из базы → канонический статус. Пустая строка и незнакомое
 * значение дают '' — «менеджер ещё не решил».
 */
export function canonTracker(raw: unknown): TrackerStatus | '' {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return '';
  return TRACKER_ALIASES[s] ?? '';
}

/**
 * Решение менеджера принято — отменять уже поздно.
 * `Uploaded` решением НЕ является: это отметка загрузки, а не вердикт.
 */
export function trackerDecided(raw: unknown): boolean {
  const c = canonTracker(raw);
  // «To be fixed» решением НЕ является: его ставит сам модератор, когда видит,
  // что сессию надо чинить. Запирать ему отмену на этом основании неверно.
  return c === 'Accepted' || c === 'Pending' || c === 'Rejected';
}

// ── Заведение модератора (provision.ts + ops.ts, 2026-08-16) ──────────────
//
// Вокабуляры ВЫХОДА, а не хранения: в колонках их нет, их вычисляет
// provisionModerator и читает бот. Живут здесь по той же причине, что и
// остальные: те же литералы иначе объявляются заново в трёх местах —
// возвращаемом типе воркера, zod-схеме ответа и разборе на стороне бота, —
// и расходятся молча, потому что расхождение видно только человеку,
// читающему сообщение в чате.

/** Роль либо уже выдана аккаунту, либо ждёт первого входа по почте. */
export const MOD_ROLE_STATE = ['membership', 'pending'] as const;
export type ModRoleState = (typeof MOD_ROLE_STATE)[number];

/** Что стало с вкладкой модератора в Google-таблице. */
export const MOD_SHEET_STATE = ['created', 'exists', 'failed'] as const;
export type ModSheetState = (typeof MOD_SHEET_STATE)[number];

// Протокол съёмки локации (2026-08-27): main = 19 упражнений (действующий),
// pilot = 16 упражнений по шаблону клиента. Колонки locations.protocol /
// participants.protocol (миграция javelin 0046). Правила — packages/shared/
// javelin/protocol.ts.
export const JAVELIN_PROTOCOL = ['main', 'pilot'] as const;
export type JavelinProtocol = (typeof JAVELIN_PROTOCOL)[number];

// «Дашборд выплат» (2026-08-26): логический вид бонуса. Хранится в
// javelin_transactions.note (префикс) и в детерминированном id `jp:<kind>:…`;
// storage-kind строки — 'period_bonus' (тиры, direction=out) или 'bonus'
// (реферальные, direction=in — в бюджет модератора).
// Итерационные тиры (period_iter) НЕ фиксируются дашбордом — их начисляет «Проект завершён» (kpi_bonus).
export const PAYOUT_KIND = ['period_week', 'referral_day', 'referral_iter'] as const;
export type PayoutKind = (typeof PAYOUT_KIND)[number];
